import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import LimaHeader from "../components/LimaHeader";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import RiskBadge from "../components/RiskBadge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileText, Search, Filter, Users, ClipboardList, ChevronRight, AlertTriangle, TrendingUp, MessageSquare, Trash2, Clock, Target, QrCode, Bell, Plus } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { LIMA_LOGO } from "../components/LimaHeader";

const STATUS_LABEL = {
  pendente_analise: { label: "Pendente análise", cls: "bg-amber-100 text-amber-800" },
  em_analise:       { label: "Em análise",        cls: "bg-blue-100 text-blue-800" },
  concluido:        { label: "Concluído",         cls: "bg-emerald-100 text-emerald-800" },
};

const CONTACT_STATUS = {
  nao_contatado: { label: "Não chamado",    cls: "bg-slate-100 text-slate-700 ring-1 ring-slate-300" },
  tentativa:     { label: "Tentativa",      cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-300" },
  agendado:      { label: "Agendado",       cls: "bg-blue-100 text-blue-800 ring-1 ring-blue-300" },
  conversado:    { label: "Conversado",     cls: "bg-teal-100 text-teal-800 ring-1 ring-teal-300" },
  sem_retorno:   { label: "Sem retorno",    cls: "bg-orange-100 text-orange-800 ring-1 ring-orange-300" },
  fechado:       { label: "Fechado",        cls: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300" },
};

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [notesDialog, setNotesDialog] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState("");
  const [qrDialog, setQrDialog] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user?.is_admin) { nav("/dashboard"); return; }
    load();
    loadTemplates();
    const t = setInterval(() => setTick(x => x + 1), 60000);
    return () => clearInterval(t);
  }, [user, loading]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/admin/note-templates");
      setTemplates(data);
    } catch { /* ignore */ }
  };

  const addTemplate = async () => {
    if (!newTemplate.trim()) return;
    try {
      const { data } = await api.post("/admin/note-templates", { text: newTemplate });
      setTemplates([...templates, data]);
      setNewTemplate("");
      toast.success("Modelo adicionado");
    } catch { toast.error("Erro ao adicionar modelo"); }
  };

  const deleteTemplate = async (tid) => {
    try {
      await api.delete(`/admin/note-templates/${tid}`);
      setTemplates(templates.filter(x => x.id !== tid));
    } catch { toast.error("Erro ao remover modelo"); }
  };

  const triggerReminders = async () => {
    try {
      await api.post("/admin/reminders/trigger");
      toast.success("Lembretes de 3+ dias sendo enviados em background");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao disparar lembretes");
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [d, f] = await Promise.all([
        api.get("/admin/diagnostics"),
        api.get("/admin/funnel"),
      ]);
      setItems(d.data);
      setFunnel(f.data);
    } catch (err) {
      toast.error("Falha ao carregar");
    } finally { setBusy(false); }
  };

  const updateStatus = async (did, status) => {
    try {
      await api.patch(`/admin/diagnostics/${did}/status`, { status });
      setItems(items.map(x => x.id === did ? { ...x, status } : x));
      toast.success("Status atualizado");
    } catch { toast.error("Erro ao atualizar"); }
  };

  const updateContactStatus = async (did, contact_status) => {
    try {
      await api.patch(`/admin/diagnostics/${did}/contact-status`, { contact_status });
      setItems(items.map(x => x.id === did ? { ...x, contact_status } : x));
      toast.success("Contato atualizado");
    } catch { toast.error("Erro ao atualizar contato"); }
  };

  const triggerDailySummary = async () => {
    try {
      await api.post("/admin/daily-summary/trigger");
      toast.success("Resumo diário enviado para o email cadastrado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar resumo");
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !notesDialog) return;
    try {
      const { data } = await api.post(`/admin/diagnostics/${notesDialog.id}/notes`, { text: newNote });
      const updated = { ...notesDialog, admin_notes: [...(notesDialog.admin_notes || []), data] };
      setNotesDialog(updated);
      setItems(items.map(x => x.id === notesDialog.id ? updated : x));
      setNewNote("");
      toast.success("Nota adicionada");
    } catch (err) { toast.error("Erro ao salvar nota"); }
  };

  const deleteNote = async (nid) => {
    if (!notesDialog) return;
    try {
      await api.delete(`/admin/diagnostics/${notesDialog.id}/notes/${nid}`);
      const updated = { ...notesDialog, admin_notes: (notesDialog.admin_notes || []).filter(n => n.id !== nid) };
      setNotesDialog(updated);
      setItems(items.map(x => x.id === notesDialog.id ? updated : x));
      toast.success("Nota removida");
    } catch { toast.error("Erro ao remover"); }
  };

  // SLA helpers
  const hoursSince = (iso) => {
    if (!iso) return 0;
    return Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  };
  const slaFor = (it) => {
    const h = hoursSince(it.created_at);
    const isHigh = it.result?.fine_band === "ALTO";
    const untouched = !it.contact_status || it.contact_status === "nao_contatado";
    if (isHigh && untouched && h >= 24) return { level: "breach", h };
    if (isHigh && untouched && h >= 12) return { level: "warning", h };
    return { level: "ok", h };
  };

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    const priority = { "ALTO": 0, "MÉDIO": 1, "BAIXO": 2 };
    return items
      .filter(it => {
        const cur = it.status || "pendente_analise";
        if (statusFilter !== "all" && cur !== statusFilter) return false;
        if (!term) return true;
        return (
          (it.company?.razao_social || "").toLowerCase().includes(term) ||
          (it.company?.cnpj || "").toLowerCase().includes(term) ||
          (it.submitted_by?.email || "").toLowerCase().includes(term) ||
          (it.contact?.nome || "").toLowerCase().includes(term) ||
          (it.contact?.email || "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        // Priority 1: fine_band ALTO first, then MÉDIO, then BAIXO
        const pa = priority[a.result?.fine_band] ?? 3;
        const pb = priority[b.result?.fine_band] ?? 3;
        if (pa !== pb) return pa - pb;
        // Priority 2: pending status first
        const sa = (a.status || "pendente_analise") === "pendente_analise" ? 0 : 1;
        const sb = (b.status || "pendente_analise") === "pendente_analise" ? 0 : 1;
        if (sa !== sb) return sa - sb;
        // Priority 3: most recent
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [items, q, statusFilter]);

  const stats = useMemo(() => ({
    total: items.length,
    pendentes: items.filter(i => (i.status || "pendente_analise") === "pendente_analise").length,
    em_analise: items.filter(i => i.status === "em_analise").length,
    concluidos: items.filter(i => i.status === "concluido").length,
    altos: items.filter(i => i.result?.fine_band === "ALTO").length,
  }), [items]);

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <LimaHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">ÁREA DO CONSULTOR</span>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#10243f] mt-1">
              Diagnósticos submetidos
            </h1>
            <p className="text-slate-600 mt-2">Todos os checklists recebidos, com status de análise.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setQrDialog(true)} data-testid="admin-qr-btn"
                    className="border-[#c7a45a] text-[#10243f]">
              <QrCode size={16} className="mr-2" /> QR Code
            </Button>
            <Button variant="outline" onClick={triggerReminders} data-testid="admin-reminders"
                    className="border-slate-300">
              <Bell size={16} className="mr-2" /> Lembretes 3+ dias
            </Button>
            <Button variant="outline" onClick={triggerDailySummary} data-testid="admin-daily-summary"
                    className="border-slate-300">
              Enviar resumo
            </Button>
            <Button variant="outline" onClick={() => nav("/dashboard")} data-testid="admin-back-dash"
                    className="border-slate-300">
              Empresas
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard icon={ClipboardList} label="Total" value={stats.total} accent="text-[#10243f]" />
          <StatCard icon={AlertTriangle} label="Multa ALTA" value={stats.altos} accent="text-red-700" highlight />
          <StatCard icon={Users} label="Pendentes" value={stats.pendentes} accent="text-amber-700" />
          <StatCard icon={Filter} label="Em análise" value={stats.em_analise} accent="text-blue-700" />
          <StatCard icon={FileText} label="Concluídos" value={stats.concluidos} accent="text-emerald-700" />
        </div>

        {funnel && (
          <div className="bg-gradient-to-br from-[#10243f] to-[#19385e] text-white rounded-xl p-6 mb-8" data-testid="funnel-panel">
            <div className="flex items-center gap-2 mb-4">
              <Target size={18} className="text-[#c7a45a]" />
              <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">FUNIL DE CONVERSÃO · {funnel.month}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <FunnelStat label="ALTO recebidos" value={funnel.alto.total} sub={`vs ${funnel.alto.prev_total} mês anterior`} />
              <FunnelStat label="ALTO fechados" value={funnel.alto.fechados} sub="clientes conquistados" gold />
              <FunnelStat label="Conversão ALTO" value={`${funnel.alto.taxa_conversao}%`} sub="dos alto risco viraram fechado" gold />
              <FunnelStat label="Total do mês" value={funnel.total_month} sub={`${funnel.total_fechados} fechados`} />
              <FunnelStat label="Conversão geral" value={`${funnel.taxa_conversao_geral}%`} sub="fechados / recebidos" />
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Buscar por empresa, CNPJ ou email"
                   value={q} onChange={e => setQ(e.target.value)}
                   className="pl-9" data-testid="admin-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[220px]" data-testid="admin-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pendente_analise">Pendentes</SelectItem>
              <SelectItem value="em_analise">Em análise</SelectItem>
              <SelectItem value="concluido">Concluídos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {stats.altos > 0 && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 mb-6 flex items-start gap-3" data-testid="admin-priority-banner">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
            <div>
              <div className="font-bold text-red-900 text-sm">
                {stats.altos} diagnóstico{stats.altos > 1 ? "s" : ""} com risco de multa ALTO — prioridade máxima
              </div>
              <div className="text-xs text-red-800 mt-0.5">
                Clientes destacados em vermelho abaixo. Recomenda-se ligar/enviar WhatsApp em até 24h.
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[#10243f]">
                <tr>
                  <th className="text-left p-3 font-semibold">Empresa</th>
                  <th className="text-left p-3 font-semibold">Contato do cliente</th>
                  <th className="text-left p-3 font-semibold">Data</th>
                  <th className="text-left p-3 font-semibold">Nível</th>
                  <th className="text-left p-3 font-semibold">Multa</th>
                  <th className="text-left p-3 font-semibold">Contato</th>
                  <th className="text-left p-3 font-semibold">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {busy && <tr><td colSpan={8} className="p-8 text-center text-slate-500">Carregando...</td></tr>}
                {!busy && filtered.length === 0 && (
                  <tr><td colSpan={8} className="p-10 text-center text-slate-500">Nenhum diagnóstico encontrado.</td></tr>
                )}
                {filtered.map(it => {
                  const s = STATUS_LABEL[it.status || "pendente_analise"];
                  const ct = it.contact || {};
                  const waNum = (ct.whatsapp || "").replace(/\D/g, "");
                  const isHighFine = it.result?.fine_band === "ALTO";
                  const rowClass = isHighFine
                    ? "border-t-2 border-red-200 bg-red-50/50 hover:bg-red-50"
                    : "border-t border-slate-100 hover:bg-slate-50";
                  return (
                    <tr key={it.id} className={rowClass} data-testid={`admin-row-${it.id}`}>
                      <td className="p-3 relative">
                        {isHighFine && (
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600" title="Prioridade alta" />
                        )}
                        <div className={`font-medium ${isHighFine ? "text-red-800" : "text-[#10243f]"}`}>
                          {isHighFine && <AlertTriangle size={12} className="inline mr-1 -mt-0.5 text-red-600" data-testid={`priority-alert-${it.id}`} />}
                          {it.company?.razao_social || "—"}
                        </div>
                        <div className="text-xs text-slate-500 font-mono">{it.company?.cnpj || "—"}</div>
                        <div className="text-xs text-slate-400">{it.company?.empregados || 0} empregados</div>
                      </td>
                      <td className="p-3">
                        {ct.nome ? (
                          <div className="space-y-0.5 text-xs">
                            <div className="font-semibold text-[#10243f]">{ct.nome}</div>
                            {ct.email && <div><a href={`mailto:${ct.email}`} className="text-blue-700 hover:underline">{ct.email}</a></div>}
                            {ct.whatsapp && (
                              <div className="flex items-center gap-1.5">
                                <span>{ct.whatsapp}</span>
                                {waNum.length >= 10 && (
                                  <a href={`https://wa.me/${waNum.startsWith("55") ? waNum : "55" + waNum}`}
                                     target="_blank" rel="noreferrer"
                                     className="text-emerald-700 font-semibold hover:underline"
                                     data-testid={`admin-wa-${it.id}`}>
                                    wa.me
                                  </a>
                                )}
                              </div>
                            )}
                            {ct.melhor_horario && <div className="text-slate-500">⏰ {ct.melhor_horario}</div>}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Sem contato</span>
                        )}
                      </td>
                      <td className="p-3 text-slate-600 text-xs">
                        {(() => {
                          const sla = slaFor(it);
                          return (
                            <div>
                              <div>{new Date(it.created_at).toLocaleString("pt-BR")}</div>
                              {sla.level !== "ok" ? (
                                <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                  sla.level === "breach"
                                    ? "bg-red-600 text-white animate-pulse ring-2 ring-red-300"
                                    : "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                                }`} data-testid={`sla-${it.id}`}>
                                  <Clock size={10} /> SLA {sla.h}h
                                  {sla.level === "breach" && " · URGENTE"}
                                </div>
                              ) : (
                                <div className="text-[10px] text-slate-400 mt-0.5">{sla.h}h atrás</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-3"><RiskBadge level={it.result?.color} label={it.result?.level} /></td>
                      <td className="p-3">
                        <span className={`font-bold text-sm ${
                          it.result?.fine_band === "ALTO" ? "text-red-700" :
                          it.result?.fine_band === "MÉDIO" ? "text-orange-700" : "text-emerald-700"
                        }`}>{it.result?.fine_band}</span>
                      </td>
                      <td className="p-3 text-xs text-slate-600">
                        <Select value={it.contact_status || "nao_contatado"} onValueChange={(v) => updateContactStatus(it.id, v)}>
                          <SelectTrigger className={`h-8 text-xs w-[140px] ${(CONTACT_STATUS[it.contact_status || "nao_contatado"] || CONTACT_STATUS.nao_contatado).cls}`}
                                         data-testid={`contact-status-${it.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(CONTACT_STATUS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {it.result?.active_modules?.length > 0 && (
                          <div className="text-[10px] text-slate-400 mt-1 truncate max-w-[140px]"
                               title={(it.result?.active_modules || []).join(", ")}>
                            {it.result.active_modules.length} NRs ativas
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <Select value={it.status || "pendente_analise"} onValueChange={(v) => updateStatus(it.id, v)}>
                          <SelectTrigger className={`h-8 text-xs w-[140px] ${s.cls}`} data-testid={`admin-status-${it.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pendente_analise">Pendente</SelectItem>
                            <SelectItem value="em_analise">Em análise</SelectItem>
                            <SelectItem value="concluido">Concluído</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost"
                                  onClick={() => setNotesDialog(it)}
                                  data-testid={`admin-notes-${it.id}`}
                                  className="relative px-2">
                            <MessageSquare size={14} />
                            {(it.admin_notes?.length || 0) > 0 && (
                              <span className="absolute -top-1 -right-1 bg-[#c7a45a] text-[#10243f] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                {it.admin_notes.length}
                              </span>
                            )}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => nav(`/relatorio/${it.id}`)} data-testid={`admin-open-${it.id}`}>
                            <ChevronRight size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <Dialog open={!!notesDialog} onOpenChange={(o) => !o && setNotesDialog(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-[#10243f]">
                Notas do consultor
                <div className="text-xs font-normal text-slate-500 mt-1">
                  {notesDialog?.company?.razao_social} · {notesDialog?.contact?.nome || "sem contato"}
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="max-h-[320px] overflow-y-auto space-y-3 pr-2">
                {(notesDialog?.admin_notes || []).length === 0 && (
                  <p className="text-sm text-slate-500 italic text-center py-8">Nenhuma nota ainda. Registre propostas, valores e combinados.</p>
                )}
                {(notesDialog?.admin_notes || []).slice().reverse().map(n => (
                  <div key={n.id} className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={`note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-xs text-slate-500">
                        <strong className="text-[#10243f]">{n.by_name || n.by}</strong> · {new Date(n.at).toLocaleString("pt-BR")}
                      </div>
                      <button onClick={() => deleteNote(n.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-note-${n.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 pt-4">
                {templates.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Modelos rápidos</div>
                    <div className="flex flex-wrap gap-2">
                      {templates.map(t => (
                        <button key={t.id} type="button"
                                onClick={() => setNewNote(prev => (prev ? prev + "\n" : "") + t.text)}
                                data-testid={`template-${t.id}`}
                                className="text-xs px-2.5 py-1 bg-[#10243f]/5 hover:bg-[#c7a45a]/20 text-[#10243f] rounded-full border border-[#10243f]/10 transition-colors">
                          {t.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Textarea placeholder="Nova nota (proposta, valor discutido, próximos passos)..."
                          value={newNote} onChange={e => setNewNote(e.target.value)}
                          rows={3} data-testid="note-input" />
                <div className="flex justify-end mt-2">
                  <Button onClick={addNote} disabled={!newNote.trim()}
                          className="bg-[#10243f] hover:bg-[#19385e] text-white"
                          data-testid="note-save">
                    Adicionar nota
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={qrDialog} onOpenChange={setQrDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-[#10243f]">QR Code do Checklist</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 p-4">
              <div className="relative bg-white p-4 rounded-lg border-2 border-[#c7a45a]" data-testid="qr-canvas-wrap">
                <QRCodeCanvas
                  value={`${window.location.origin}/checklist`}
                  size={280}
                  level="H"
                  imageSettings={{
                    src: LIMA_LOGO,
                    height: 60,
                    width: 60,
                    excavate: true,
                  }}
                  bgColor="#ffffff"
                  fgColor="#10243f"
                />
              </div>
              <p className="text-sm text-center text-slate-600">
                Aponte a câmera para acessar o diagnóstico gratuito.
              </p>
              <p className="text-xs text-center text-slate-500 font-mono break-all">
                {window.location.origin}/checklist
              </p>
              <Button data-testid="qr-download"
                      onClick={() => {
                        const canvas = document.querySelector('[data-testid="qr-canvas-wrap"] canvas');
                        if (!canvas) return;
                        const link = document.createElement("a");
                        link.download = "lima-sst-qrcode.png";
                        link.href = canvas.toDataURL("image/png");
                        link.click();
                      }}
                      className="bg-[#10243f] hover:bg-[#19385e] text-white">
                Baixar PNG para cartão de visita
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, highlight }) {
  return (
    <div className={`bg-white border rounded-xl p-5 ${highlight ? "border-red-300 bg-red-50/50 ring-2 ring-red-100" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest font-semibold">
        <Icon size={14} /> {label}
      </div>
      <div className={`font-display font-black text-3xl mt-2 ${accent}`}>{value}</div>
    </div>
  );
}

function FunnelStat({ label, value, sub, gold }) {
  return (
    <div className={`p-4 rounded-lg ${gold ? "bg-[#c7a45a]/20 ring-1 ring-[#c7a45a]/40" : "bg-white/5 ring-1 ring-white/10"}`}>
      <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{label}</div>
      <div className={`font-display font-black text-3xl mt-1 ${gold ? "text-[#e4c77b]" : "text-white"}`}>{value}</div>
      <div className="text-[11px] text-white/60 mt-1">{sub}</div>
    </div>
  );
}
